import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Save, Receipt, LogOut, Loader, X } from 'lucide-react';
import { motion } from 'framer-motion';
import BurgerMenu from './BurgerMenu';
import BackButton from './BackButton';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabase';
import { SUPABASE_CONFIG } from '../config/supabase';
import { formatAmount, type Transaction } from '../services/paymentService';

interface ActiveSubscription {
  id: string;
  plan_id: string;
  status: string;
  subscription_code?: string;
  current_period_end?: string;
  payment_plans: {
    id: string;
    plan_code: string;
    name: string;
    tour_credits: number;
    amount: number;
    currency: string;
  };
}

const Account: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [activeSubscription, setActiveSubscription] = useState<ActiveSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [newAvatar, setNewAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [visibleTransactions, setVisibleTransactions] = useState(3);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.full_name || '');
      setEmail(profile.email || '');
    } else if (user) {
      setEmail(user.email || '');
      setFirstName(user.user_metadata?.full_name || '');
    }
  }, [profile, user]);

  // Reset avatar error when profile avatar changes or preview changes
  useEffect(() => {
    setAvatarError(false);
  }, [profile?.avatar_url, avatarPreview]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setNewAvatar(file);
      setRemoveAvatar(false);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setRemoveAvatar(true);
    setNewAvatar(null);
    setAvatarPreview(null);
  };

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    
    try {
      let avatarUrl = profile?.avatar_url;

      // Remove avatar if requested
      if (removeAvatar) {
        avatarUrl = null;
      }
      // Upload new avatar if selected
      else if (newAvatar) {
        setUploadingAvatar(true);
        
        // Create unique filename
        const fileExt = newAvatar.name.split('.').pop();
        const fileName = `${user.id}-${Date.now()}.${fileExt}`;
        const filePath = `avatars/${fileName}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, newAvatar, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading avatar:', uploadError);
          alert('Failed to upload avatar. Please try again.');
          setUploadingAvatar(false);
          setIsSaving(false);
          return;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        if (!publicUrl) {
          console.error('Failed to get public URL for uploaded avatar');
          alert('Failed to get avatar URL. Please try again.');
          setUploadingAvatar(false);
          setIsSaving(false);
          return;
        }

        avatarUrl = publicUrl;
        console.log('Avatar uploaded successfully:', publicUrl);
        setUploadingAvatar(false);
      }

      // Update user profile in the database
      const updateData: any = {
        full_name: firstName,
        updated_at: new Date().toISOString()
      };

      if (removeAvatar || (avatarUrl && avatarUrl !== profile?.avatar_url)) {
        updateData.avatar_url = avatarUrl;
      }

      console.log('Updating user profile with data:', updateData);

      const { error, data } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Error updating profile in database:', error);
        alert(`Failed to update profile: ${error.message}. Please try again.`);
        setIsSaving(false);
        return;
      }

      if (!data || data.length === 0) {
        console.error('Profile update returned no data - possible RLS issue');
        alert('Profile update may have failed. Please refresh the page to verify.');
      }

      console.log('Profile updated successfully:', data);

      // Refresh profile data with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile refresh timeout')), 5000)
      );
      
      try {
        await Promise.race([refreshProfile(), timeoutPromise]);
        setIsEditing(false);
        setNewAvatar(null);
        setAvatarPreview(null);
        setRemoveAvatar(false);
        // Show success message
        alert('Profile updated successfully!');
      } catch (refreshError) {
        console.error('Error refreshing profile:', refreshError);
        // Still consider it a success if the update worked, just refresh failed
        alert('Profile updated successfully! Please refresh the page to see changes.');
        setIsEditing(false);
        setNewAvatar(null);
        setAvatarPreview(null);
        setRemoveAvatar(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
      setUploadingAvatar(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
      setIsSigningOut(false);
    }
  };

  const handleLoadMore = () => {
    setVisibleTransactions(prev => prev + 5);
  };

  // Fetch active subscription
  useEffect(() => {
    const fetchActiveSubscription = async () => {
      if (!user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch(
          `${SUPABASE_CONFIG.url}/functions/v1/get-active-subscription?user_id=${user.id}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const data = await response.json();

        if (data.status === 'success' && data.has_active_subscription) {
          setActiveSubscription(data.subscription);
        } else {
          setActiveSubscription(null);
        }
      } catch (error) {
        console.error('Error fetching active subscription:', error);
        setActiveSubscription(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchActiveSubscription();
  }, [user]);

  // Fetch purchase history
  useEffect(() => {
    const fetchTransactions = async () => {
      // Don't fetch if user not authenticated
      if (!user) {
        setLoadingTransactions(false);
        return;
      }

      try {
        setLoadingTransactions(true);
        
        // Use real authenticated user ID
        const userId = user.id;
        console.log('📜 Fetching transactions for authenticated user:', userId);

        // Call Edge Function (secure - enforces RLS)
        const response = await fetch(
          `${SUPABASE_CONFIG.url}${SUPABASE_CONFIG.endpoints.getTransactions}?user_id=${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`,
            },
          }
        );

        const result = await response.json();

        if (result.status === 'success') {
          setTransactions(result.transactions || []);
        } else {
          console.error('Error fetching transactions:', result.error);
        }
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [user]);

  return (
    <div className="app">
      <div className="header">
        <BackButton onClick={() => navigate('/home')} />
        <h2 className="header-title">My Account</h2>
        <BurgerMenu />
      </div>
      
      <div className="container">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Form Section */}
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid var(--border-color)',
            marginBottom: '24px'
          }}>
            {/* Profile Picture */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '32px',
              paddingBottom: '24px',
              borderBottom: '1px solid var(--border-color)'
            }}>
              <div style={{
                position: 'relative',
                width: '120px',
                height: '120px',
                marginBottom: isEditing ? '16px' : '0'
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '4px solid var(--primary-color)',
                  backgroundColor: 'var(--secondary-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {removeAvatar || (!avatarPreview && !profile?.avatar_url) || avatarError ? (
                    <img
                      src={`${process.env.PUBLIC_URL}/PFP.svg`}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <img
                      src={avatarPreview || profile?.avatar_url || ''}
                      alt="Profile"
                      onError={() => setAvatarError(true)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                </div>
                {uploadingAvatar && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Loader size={32} color="white" className="animate-spin" />
                  </div>
                )}
              </div>

              {isEditing && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <label style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--secondary-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--secondary-color)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                    >
                      <User size={14} />
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                    
                    {profile?.avatar_url && !removeAvatar && (
                      <button
                        onClick={handleRemoveAvatar}
                        type="button"
                        style={{
                          padding: '8px 16px',
                          backgroundColor: 'transparent',
                          border: '1px solid #EF4444',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#EF4444',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#EF4444';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#EF4444';
                        }}
                      >
                        <X size={14} />
                        Remove Image
                      </button>
                    )}
                  </div>
                  
                  {newAvatar && (
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--text-secondary)'
                    }}>
                      {newAvatar.name}
                    </span>
                  )}
                  
                  {removeAvatar && (
                    <span style={{
                      fontSize: '11px',
                      color: '#EF4444'
                    }}>
                      Image will be removed
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* First Name Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                <User size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle', transform: 'translateY(0.5px)' }} />
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={!isEditing}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  backgroundColor: isEditing ? 'var(--background)' : 'var(--secondary-color)',
                  color: 'var(--text-primary)',
                  cursor: isEditing ? 'text' : 'not-allowed',
                  transition: 'all 0.2s ease'
                }}
              />
            </div>

            {/* Email Field */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px'
              }}>
                <Mail size={16} style={{ display: 'inline', marginRight: '6px', verticalAlign: 'middle', transform: 'translateY(0.5px)' }} />
                Email Address
              </label>
              <input
                type="email"
                value={email}
                disabled={true}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  fontSize: '16px',
                  backgroundColor: 'var(--secondary-color)',
                  color: 'var(--text-secondary)',
                  cursor: 'not-allowed'
                }}
              />
              <p style={{
                fontSize: '12px',
                color: 'var(--text-light)',
                margin: '8px 0 0 0',
                lineHeight: '1.4'
              }}>
                🔒 Email provided by Google, cannot be changed.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: isEditing ? 'space-between' : 'center'
            }}>
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to original values
                      if (profile) {
                        setFirstName(profile.full_name || '');
                      }
                      setNewAvatar(null);
                      setAvatarPreview(null);
                      setRemoveAvatar(false);
                    }}
                    disabled={isSaving}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: 'var(--secondary-color)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isSaving ? 0.5 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      backgroundColor: 'var(--primary-color)',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'white',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: isSaving ? 0.5 : 1
                    }}
                  >
                    {isSaving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                    {uploadingAvatar ? 'Uploading...' : isSaving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>

          {/* Purchase History Section */}
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid var(--border-color)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Receipt size={20} />
              Purchase History
            </h3>

            <div>
              {loadingTransactions ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  gap: '12px',
                  minHeight: '120px'
                }}>
                  <Loader size={24} className="animate-spin" />
                  <span>Loading purchase history...</span>
                </div>
              ) : transactions.length === 0 ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '32px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  minHeight: '120px'
                }}>
                  No payment history to display
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {transactions.slice(0, visibleTransactions).map((transaction) => (
                      <div
                        key={transaction.id}
                        style={{
                          padding: '12px',
                          backgroundColor: 'var(--secondary-color)',
                          borderRadius: '10px',
                          border: '1px solid var(--border-color)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        className="purchase-item-hover"
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: 'var(--text-primary)',
                            marginBottom: '3px'
                          }}>
                            {transaction.description.split(' - ')[0]}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: 'var(--text-secondary)'
                          }}>
                            {new Date(transaction.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--text-primary)',
                          whiteSpace: 'nowrap',
                          marginLeft: '12px'
                        }}>
                          {transaction.display_amount || formatAmount(transaction.amount, transaction.currency)}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {transactions.length > visibleTransactions && (
                    <button
                      onClick={handleLoadMore}
                      style={{
                        width: '100%',
                        padding: '12px 20px',
                        marginTop: '16px',
                        backgroundColor: 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      View More
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Active Subscriptions Section */}
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid var(--border-color)',
            marginTop: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Receipt size={20} />
              Active Subscriptions
            </h3>

            {loadingSubscription ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                gap: '12px',
                minHeight: '120px'
              }}>
                <Loader size={24} className="animate-spin" />
                <span>Loading subscriptions...</span>
              </div>
            ) : activeSubscription ? (
              <div style={{
                padding: '16px',
                backgroundColor: 'var(--secondary-color)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '6px'
                  }}>
                    {activeSubscription.payment_plans.name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    marginBottom: '4px'
                  }}>
                    {activeSubscription.payment_plans.tour_credits} tours monthly
                  </div>
                  {activeSubscription.current_period_end && (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)'
                    }}>
                      Renews {new Date(activeSubscription.current_period_end).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => navigate('/buy-credits', { state: { tab: 'subscription' } })}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Manage
                </button>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                minHeight: '120px'
              }}>
                No active subscriptions
              </div>
            )}
          </div>

          {/* Sign Out Section */}
          <div style={{
            backgroundColor: 'var(--card-background)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid var(--border-color)',
            marginTop: '24px'
          }}>
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              style={{
                width: '100%',
                padding: '12px 20px',
                backgroundColor: '#EF4444',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                cursor: isSigningOut ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isSigningOut ? 0.6 : 1
              }}
            >
              <LogOut size={18} />
              {isSigningOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>

          {/* Footer */}
          <div style={{
            textAlign: 'center',
            padding: '24px 0 20px 0',
            fontSize: '12px',
            color: 'var(--text-secondary)',
            opacity: 0.7,
            lineHeight: '1.6'
          }}>
            <div>© 2025 Miaitor - All Rights Reserved</div>
            <a 
              href="https://SalvBarras.com" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: 'var(--text-secondary)',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.7';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Designed & Developed With ❤️ By: Salvatore
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Account; 